import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { Box, Button, Drawer, Grid, IconButton, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';

import { useTranslation } from 'react-i18next';

import { SubscriptionEdit } from '../model/subscription';

interface SubscriptionEditProps {
  subscription: SubscriptionEdit | null;
  open: boolean;
  onClose: () => void;
  handleSubscriptionChange: (date: DateTime | null, subKey: string) => void;
}

const SubscriptionEditDrawer = ({
  subscription,
  open,
  onClose,
  handleSubscriptionChange,
}: SubscriptionEditProps) => {
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);

  const { t } = useTranslation();

  const handleOnSave = () => {
    if (subscription?.key) {
      handleSubscriptionChange(selectedDate, subscription.key);
    }
  };

  useEffect(() => {
    if (subscription?.endDate && subscription.endDate !== null) {
      setSelectedDate(DateTime.fromJSDate(subscription.endDate));
    } else {
      setSelectedDate(null);
    }
  }, [subscription?.endDate]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, paddingRight: 4, paddingLeft: 4 }}>
        <Toolbar
          disableGutters
          sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginBottom: 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              marginBottom: 2,
            }}
          >
            <Typography variant="h4">
              {subscription?.key && t(`subscriptions.${subscription.key}`)}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Toolbar>
        {subscription && (
          <Grid container spacing={2} sx={{ marginTop: 2, marginBottom: 2 }}>
            <Grid item xs={12}>
              <DatePicker
                sx={{ width: '100%' }}
                onChange={(date) => setSelectedDate(date)}
                value={selectedDate}
                format="dd/MM/yyyy"
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={() =>
                  setSelectedDate(DateTime.local().plus({ years: 100 }).startOf('day'))
                }
              >
                {t('subscriptionScreen.continuously')}
              </Button>
              <Button
                variant="contained"
                onClick={() => setSelectedDate(DateTime.local().startOf('day'))}
              >
                Idag
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: 'auto',
          paddingBottom: 2,
          paddingRight: 4,
          '* + *': { marginLeft: '1rem' },
        }}
      >
        <Button onClick={onClose} variant="contained">
          {t('subscriptionScreen.cancel')}
        </Button>
        <Button onClick={handleOnSave} variant="contained">
          {t('subscriptionScreen.update')}
        </Button>
      </Box>
    </Drawer>
  );
};

export default SubscriptionEditDrawer;
